import type { Action } from "@judo/actions";
import type { PageContainer, PageDefinition } from "@judo/model-api";
import { Box, Typography } from "@mui/material";
import { usePageTitle } from "../hooks/use-page-title";
import { ButtonGroupRenderer } from "./ButtonGroupRenderer";

export interface PageHeaderProps {
	page: PageDefinition;
	editMode?: boolean;
	onDispatch?: (action: Action) => Promise<unknown>;
}

/**
 * Page header component that displays page title and page-level action button groups.
 * Action button groups come from container.actionButtonGroups.
 */
export function PageHeader({ page, editMode, onDispatch }: PageHeaderProps) {
	const container = page.container as PageContainer;
	const { title: pageTitle } = usePageTitle(page);
	const actionButtonGroups = container.actionButtonGroups ?? [];

	// Only render if there's a title or actions
	if (!pageTitle && actionButtonGroups.length === 0) {
		return null;
	}

	return (
		<Box
			data-testid={`page-header::${page.name}`}
			sx={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				mb: 2,
				pb: 1,
				borderBottom: "1px solid",
				borderColor: "divider",
				animation: "judoFadeIn 0.3s cubic-bezier(0.4,0,0.2,1) both",
			}}
		>
			{/* Page Title */}
			{pageTitle && (
				<Typography variant="h5" component="h1" data-testid={`page-header::${page.name}::title`}>
					{pageTitle}
				</Typography>
			)}

			{/* Page Action Button Groups */}
			{actionButtonGroups.length > 0 && (
				<Box data-testid={`page-header::${page.name}::actions`}>
					{actionButtonGroups.map((group) => (
						<ButtonGroupRenderer
							key={group["xmi:id"]}
							element={group}
							page={page}
							editMode={editMode}
							onDispatch={onDispatch}
						/>
					))}
				</Box>
			)}
		</Box>
	);
}
